/**
 * Default implementation of safe redirect URL validation
 * This is a basic implementation that apps can override
 */
export const createDefaultSafeRedirectUrl = (allowedUrls: string[] = ['/']) => {
  return (url: string, defaultUrl: string = '/'): string => {
    try {
      // Remove any leading/trailing whitespace
      const trimmedUrl = url.trim();

      // Parse the URL to extract just the pathname (ignore query params and hash)
      const urlObj = new URL(trimmedUrl, window.location.origin);
      const pathname = urlObj.pathname;

      // Return pathname if it's in our allowlist, otherwise return default
      return allowedUrls.includes(pathname) ? pathname : defaultUrl;
    } catch {
      // If URL parsing fails, return default
      return defaultUrl;
    }
  };
};

/**
 * Passthrough implementation that allows all URLs without validation
 * Used when no security restrictions are needed
 */
export const createPassthroughSafeRedirectUrl = () => {
  return (url: string, defaultUrl: string = '/'): string => {
    try {
      // Remove any leading/trailing whitespace
      const trimmedUrl = url.trim();

      // Parse the URL to extract just the pathname (ignore query params and hash)
      const urlObj = new URL(trimmedUrl, window.location.origin);
      return urlObj.pathname + urlObj.search; // Include query params
    } catch {
      // If URL parsing fails, return default
      return defaultUrl;
    }
  };
};

/**
 * Utility to validate if a URL is safe for redirect
 */
export const isValidRedirectUrl = (url: string, allowedUrls: string[]): boolean => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return allowedUrls.includes(urlObj.pathname);
  } catch {
    return false;
  }
};

/**
 * Extract pathname from URL safely
 */
export const extractPathnameFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url, window.location.origin);
    return urlObj.pathname;
  } catch {
    return null;
  }
};
